import React, { useMemo, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { LogOut, FileDown, Package, ClipboardList, PackageX, ReceiptText, HeartHandshake, AlertTriangle, CheckCircle2, Clock, MessageCircle, Bell, Plus, ChevronRight, ArrowLeft, ShoppingCart, KeyRound, FolderOpen, FileText, Eye } from "lucide-react";
import { useApp, formatBRL, formatDate } from "../context/AppContext";
import { Button } from "../components/ui/button";
import { useToast } from "../hooks/use-toast";
import { ChatWindow } from "../components/chat/ChatWindow";
import { FloatingChat } from "../components/chat/FloatingChat";
import { FotoPerfilCard } from "../components/FotoPerfilCard";
import { gerarFaturaPDF } from "../utils/faturaPdf";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Secao = ({ icon: Icon, titulo, children, tint = "text-blue-600" }) => (
  <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
    <div className="flex items-center gap-2 px-5 py-3.5 border-b border-slate-100">
      <Icon className={`w-4 h-4 ${tint}`} />
      <h3 className="font-semibold text-slate-800 text-sm">{titulo}</h3>
    </div>
    <div className="p-4">{children}</div>
  </div>
);

// Chat inline do portal (lista de conversas + janela) — mesma API do widget flutuante
const PortalMensagens = () => {
  const [conversas, setConversas] = useState([]);
  const [sel, setSel] = useState(null);
  const [nova, setNova] = useState(false);
  const [staff, setStaff] = useState([]);

  const carregar = useCallback(async () => {
    try { const r = await axios.get(`${API}/chat/conversas`); setConversas(r.data || []); } catch (e) { /* ignore */ }
  }, []);
  useEffect(() => { carregar(); const t = setInterval(carregar, 8000); return () => clearInterval(t); }, [carregar]);

  const abrirNova = async () => {
    setNova(true);
    try { const r = await axios.get(`${API}/chat/staff`); setStaff(r.data || []); } catch (e) { /* ignore */ }
  };
  const iniciar = async (s) => {
    try { const r = await axios.post(`${API}/chat/conversas`, { staffId: s.id }); setNova(false); setSel(r.data); carregar(); } catch (e) { /* ignore */ }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 h-[calc(100vh-16rem)] min-h-[440px]">
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden flex flex-col">
        <button onClick={abrirNova} data-testid="portal-nova-conversa"
          className="w-full flex items-center gap-2 px-4 py-3 text-sm font-medium text-blue-600 hover:bg-blue-50 border-b border-slate-100">
          <Plus className="w-4 h-4" /> Nova conversa
        </button>
        <div className="flex-1 overflow-y-auto">
          {conversas.length === 0 && <p className="text-sm text-slate-400 p-4 text-center">Nenhuma conversa. Toque em "Nova conversa".</p>}
          {conversas.map((c) => (
            <button key={c.id} onClick={() => { setSel(c); setNova(false); }} data-testid={`portal-conv-${c.id}`}
              className={`w-full text-left px-4 py-3 border-b border-slate-50 hover:bg-slate-50 ${sel?.id === c.id ? "bg-blue-50" : ""}`}>
              <div className="flex items-center justify-between">
                <span className="font-medium text-slate-800 text-sm truncate">{c.staffNome || c.staffLabel}</span>
                {c.naoLidas > 0 && <span className="text-[10px] font-bold text-white bg-red-500 rounded-full px-1.5 py-0.5">{c.naoLidas}</span>}
              </div>
              <p className="text-xs text-slate-400 truncate">{c.ultimaMsg || c.staffLabel}{c.status === "encerrada" ? " · encerrada" : ""}</p>
            </button>
          ))}
        </div>
      </div>

      <div className="md:col-span-2 bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden flex flex-col min-h-0">
        {nova ? (
          <div className="flex-1 overflow-y-auto">
            <div className="px-4 py-2.5 border-b border-slate-100 flex items-center gap-2 text-sm text-slate-600">
              <button onClick={() => setNova(false)} className="p-1 rounded hover:bg-slate-100"><ArrowLeft className="w-4 h-4" /></button>
              <span className="font-medium">Falar com</span>
            </div>
            {staff.length === 0 ? <p className="text-sm text-slate-400 p-6 text-center">Nenhum atendente disponível.</p> :
              staff.map((s) => (
                <button key={s.id} onClick={() => iniciar(s)} data-testid={`portal-staff-${s.id}`}
                  className="w-full flex items-center justify-between px-4 py-3 border-b border-slate-50 hover:bg-slate-50 text-left">
                  <div><p className="text-sm font-medium text-slate-800">{s.nome}</p><p className="text-xs text-slate-400">{s.label}</p></div>
                  <ChevronRight className="w-4 h-4 text-slate-300" />
                </button>
              ))}
          </div>
        ) : sel ? (
          <ChatWindow key={sel.id} conversa={sel} meLado="familia" onBack={() => setSel(null)} onChanged={carregar} />
        ) : (
          <div className="flex-1 flex items-center justify-center text-slate-400 text-sm flex-col gap-2">
            <MessageCircle className="w-8 h-8 text-slate-200" /> Selecione ou inicie uma conversa
          </div>
        )}
      </div>
    </div>
  );
};

const PortalDocumentos = ({ residente }) => {
  const { getDocumentoFamiliaBlob } = useApp();
  const { toast } = useToast();
  const docs = residente?.documentos || [];
  const [thumbs, setThumbs] = useState({});
  useEffect(() => {
    let ativo = true;
    docs.filter((d) => d.thumb && !thumbs[d.id]).forEach((d) => {
      getDocumentoFamiliaBlob(residente.id, d.id, true).then((b) => ativo && setThumbs((t) => ({ ...t, [d.id]: URL.createObjectURL(b) }))).catch(() => {});
    });
    return () => { ativo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [residente?.id, docs.map((d) => d.id).join(",")]);
  const abrir = async (d) => {
    const w = window.open("", "_blank");
    if (w) w.document.write("<p style='font-family:sans-serif;color:#64748b;padding:24px'>Carregando documento...</p>");
    try {
      const blob = await getDocumentoFamiliaBlob(residente.id, d.id);
      const url = URL.createObjectURL(blob);
      if (w) w.location.href = url; else window.open(url, "_blank");
    } catch (e) { if (w) w.close(); toast({ title: "Não foi possível abrir o documento" }); }
  };
  const grupos = docs.reduce((m, d) => { const c = d.classe || "Outros"; (m[c] = m[c] || []).push(d); return m; }, {});
  return (
    <Secao icon={FolderOpen} titulo="Documentos compartilhados" tint="text-emerald-600">
      {docs.length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-6" data-testid="familia-docs-vazio">Nenhum documento foi compartilhado com você até o momento.</p>
      ) : (
        <div className="space-y-5" data-testid="familia-docs-lista">
          {Object.entries(grupos).map(([classe, lista]) => (
            <div key={classe}>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">{classe}</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {lista.map((d) => (
                  <button key={d.id} type="button" onClick={() => abrir(d)} className="text-left rounded-lg border border-slate-100 bg-slate-50/60 overflow-hidden hover:border-blue-300 hover:shadow-sm transition-all" data-testid={`familia-doc-${d.id}`}>
                    <div className={`h-28 flex items-center justify-center ${thumbs[d.id] ? "bg-slate-100" : d.tipo === "pdf" ? "bg-red-50 text-red-500" : "bg-blue-50 text-blue-500"}`}>
                      {thumbs[d.id] ? <img src={thumbs[d.id]} alt={d.nome} className="w-full h-full object-cover object-top" /> : <FileText className="w-8 h-8" />}
                    </div>
                    <div className="px-3 py-2.5">
                      <p className="text-sm font-medium text-slate-700 truncate" title={d.nome}>{d.nome}</p>
                      <p className="text-xs text-slate-400 uppercase flex items-center gap-1"><Eye className="w-3 h-3" /> {d.tipo}{d.criadoEm ? <span className="normal-case"> · {d.criadoEm.slice(0, 10).split("-").reverse().join("/")}</span> : null}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </Secao>
  );
};

const FamiliaPortal = () => {
  const { auth, logout, residentes, produtos, consumos, faturas, empresa,
    getProduto, getResidente, estoqueDoResidente } = useApp();
  const data = useApp();
  const fornecimentos = data.fornecimentos || [];
  const [rid, setRid] = useState(residentes[0]?.id || null);
  const [tab, setTab] = useState("mensagens");
  const [naoLidas, setNaoLidas] = useState(0);

  const carregarNaoLidas = useCallback(async () => {
    try { const r = await axios.get(`${API}/chat/nao-lidas`); setNaoLidas(r.data.total || 0); } catch (e) { /* ignore */ }
  }, []);
  useEffect(() => { carregarNaoLidas(); const t = setInterval(carregarNaoLidas, 15000); return () => clearInterval(t); }, [carregarNaoLidas]);

  const residente = getResidente(rid) || residentes[0];
  const rId = residente?.id;

  const meusFornec = useMemo(() => fornecimentos.filter((f) => f.residenteId === rId), [fornecimentos, rId]);
  const meusConsumos = useMemo(() => consumos.filter((c) => c.residenteId === rId), [consumos, rId]);
  const minhasFaturas = useMemo(() => faturas.filter((f) => f.residenteId === rId && !f.cancelada), [faturas, rId]);

  const estoqueFamiliar = useMemo(() => {
    if (!rId) return [];
    return produtos
      .map((p) => ({ p, qtd: estoqueDoResidente(rId, p.id) }))
      .filter((x) => x.qtd > 0 || meusFornec.some((f) => f.produtoId === x.p.id));
  }, [produtos, rId, meusFornec, estoqueDoResidente]);

  const alertasEstoque = estoqueFamiliar.filter((x) => x.qtd <= 2);

  const consumosCasa = useMemo(() => meusConsumos.filter((c) => (c.qtdEmpresa || 0) > 0), [meusConsumos]);
  const totalCasa = useMemo(() => consumosCasa.reduce((s, c) => s + (c.valorCobrado || 0), 0), [consumosCasa]);

  const mesAtual = new Date().toISOString().slice(0, 7);
  const faturaAtual = useMemo(() => minhasFaturas.find((f) => f.mes === mesAtual), [minhasFaturas, mesAtual]);
  const consumoMes = useMemo(() => consumosCasa.filter((c) => (c.data || "").slice(0, 7) === mesAtual).reduce((s, c) => s + (c.valorCobrado || 0), 0), [consumosCasa, mesAtual]);
  const totalParcial = faturaAtual ? faturaAtual.total : consumoMes;
  const mensalidadeParcial = Math.max(0, totalParcial - consumoMes);

  if (!residente) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="text-center">
          <p className="text-slate-600 font-medium">Nenhum residente vinculado ao seu acesso.</p>
          <Button variant="outline" className="mt-4 gap-2" onClick={logout}><LogOut className="w-4 h-4" /> Sair</Button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: "mensagens", label: "Mensagens", icon: MessageCircle, badge: naoLidas },
    { id: "financeiro", label: "Financeiro", icon: ReceiptText },
    { id: "produtos", label: "Produtos", icon: Package },
    { id: "documentos", label: "Documentos", icon: FolderOpen, badge: (residente.documentos || []).length },
    { id: "senha", label: "Senha", icon: KeyRound },
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="familia-portal">
      <header className="bg-white border-b border-slate-100 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {empresa?.logo ? <img src={empresa.logo} alt="logo" className="w-9 h-9 rounded-lg object-cover" />
              : <div className="w-9 h-9 rounded-lg bg-blue-600 flex items-center justify-center"><HeartHandshake className="w-5 h-5 text-white" /></div>}
            <div>
              <p className="font-semibold text-slate-800 text-sm leading-tight">{empresa?.nome || "Casa de Repouso São Jorge"}</p>
              <p className="text-xs text-slate-400">Portal do Familiar · {auth?.nome}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setTab("mensagens")} className="relative p-2 rounded-lg hover:bg-slate-100 transition-colors" data-testid="familia-sino">
              <Bell className="w-5 h-5 text-slate-500" />
              {naoLidas > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-bold text-white bg-red-500 rounded-full" data-testid="familia-sino-badge">{naoLidas}</span>}
            </button>
            <Button variant="outline" size="sm" className="gap-2" onClick={logout} data-testid="familia-logout"><LogOut className="w-4 h-4" /> Sair</Button>
          </div>
        </div>
        <div className="max-w-5xl mx-auto px-4 flex gap-1 border-t border-slate-50">
          {tabs.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`familia-tab-${t.id}`}
              className={`relative flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === t.id ? "border-blue-600 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
              <t.icon className="w-4 h-4" /> {t.label}
              {t.badge > 0 && <span className="ml-0.5 text-[10px] font-bold text-white bg-red-500 rounded-full px-1.5">{t.badge}</span>}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6 space-y-5">
        {residentes.length > 1 && tab !== "mensagens" && (
          <div className="flex gap-2 flex-wrap">
            {residentes.map((r) => (
              <button key={r.id} onClick={() => setRid(r.id)} data-testid={`familia-residente-${r.id}`}
                className={`px-3.5 py-2 rounded-lg text-sm font-medium border transition-colors ${r.id === rId ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
                {r.nome}
              </button>
            ))}
          </div>
        )}

        {tab !== "mensagens" && (
          <div className="bg-gradient-to-r from-blue-600 to-blue-500 rounded-xl p-5 text-white">
            <p className="text-xs text-blue-100">Residente</p>
            <p className="text-xl font-bold">{residente.nome}</p>
            {residente.quarto && <p className="text-sm text-blue-100 mt-0.5">Quarto {residente.quarto}</p>}
          </div>
        )}

        {tab === "mensagens" && <PortalMensagens />}
        {tab === "documentos" && <PortalDocumentos residente={residente} />}

        {tab === "financeiro" && (
          <>
            <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-5" data-testid="familia-resumo-mes">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Resumo do mês ({mesAtual.split("-").reverse().join("/")})</p>
              <div className="mt-3 space-y-1.5 text-sm">
                <div className="flex items-center justify-between"><span className="text-slate-600">Mensalidade e encargos</span><span className="font-medium text-slate-800">{formatBRL(mensalidadeParcial)}</span></div>
                <div className="flex items-center justify-between"><span className="text-slate-600">Consumo da casa (materiais/medicamentos)</span><span className="font-medium text-amber-700">{formatBRL(consumoMes)}</span></div>
                <div className="flex items-center justify-between border-t border-slate-100 pt-2 mt-1"><span className="font-semibold text-slate-700">Total parcial</span><span className="text-lg font-bold text-blue-700">{formatBRL(totalParcial)}</span></div>
              </div>
              <p className="text-[11px] text-slate-400 mt-2 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Valor parcial — a fatura ainda pode receber outros lançamentos até o fechamento.</p>
            </div>
            <Secao icon={ReceiptText} titulo="Faturas" tint="text-emerald-600">
            {minhasFaturas.length === 0 ? <p className="text-sm text-slate-400 py-4 text-center">Nenhuma fatura.</p> : (
              <div className="space-y-2">
                {minhasFaturas.map((f) => (
                  <div key={f.key} className="flex items-center justify-between border border-slate-100 rounded-lg px-3.5 py-2.5" data-testid={`familia-fatura-${f.key}`}>
                    <div>
                      <p className="text-sm font-medium text-slate-700">{f.mes.split("-").reverse().join("/")}</p>
                      <p className="text-xs flex items-center gap-1">
                        {f.paga ? <span className="text-emerald-600 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Paga</span>
                          : <span className="text-amber-600 flex items-center gap-1"><Clock className="w-3 h-3" /> Em aberto · vence {formatDate(f.vencimento)}</span>}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-800 text-sm">{formatBRL(f.total)}</span>
                      <Button size="sm" variant="outline" className="gap-1.5" onClick={() => gerarFaturaPDF({ empresa, residente, fatura: f, getProduto })} data-testid={`familia-pdf-${f.key}`}>
                        <FileDown className="w-3.5 h-3.5" /> PDF
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Secao>
          </>
        )}

        {tab === "produtos" && (
          <>
            {alertasEstoque.length > 0 && (
              <div className="bg-amber-50 border border-amber-100 rounded-xl p-4" data-testid="familia-alerta-estoque">
                <p className="flex items-center gap-2 font-semibold text-amber-800 text-sm mb-2"><AlertTriangle className="w-4 h-4" /> Itens do estoque familiar acabando</p>
                <div className="flex flex-wrap gap-2">
                  {alertasEstoque.map((x) => (
                    <span key={x.p.id} className={`text-xs font-medium px-2.5 py-1 rounded-md ${x.qtd <= 0 ? "text-red-700 bg-red-100" : "text-amber-700 bg-amber-100"}`}>
                      {x.p.nome}: {x.qtd <= 0 ? "zerado" : `${x.qtd} restante(s)`}
                    </span>
                  ))}
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <Secao icon={ShoppingCart} titulo="Consumido da casa (será cobrado na fatura)" tint="text-amber-600">
                {consumosCasa.length === 0 ? <p className="text-sm text-slate-400 py-4 text-center">Nenhum item da casa consumido.</p> : (
                  <div className="overflow-x-auto" data-testid="familia-consumo-casa">
                    <table className="w-full text-sm">
                      <thead><tr className="text-left text-slate-400 border-b border-slate-100">
                        <th className="py-2 font-medium">Data</th><th className="py-2 font-medium">Produto</th><th className="py-2 font-medium text-right">Qtd</th><th className="py-2 font-medium text-right">Valor</th>
                      </tr></thead>
                      <tbody className="divide-y divide-slate-50">
                        {consumosCasa.map((c) => (
                          <tr key={c.id}>
                            <td className="py-2 text-slate-500">{formatDate(c.data)}</td>
                            <td className="py-2 text-slate-700">{getProduto(c.produtoId)?.nome || "-"}</td>
                            <td className="py-2 text-right text-slate-700">{c.qtdEmpresa}</td>
                            <td className="py-2 text-right font-medium text-amber-700">{formatBRL(c.valorCobrado || 0)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot><tr className="border-t border-slate-100"><td colSpan={3} className="py-2 text-right font-semibold text-slate-600">Total a cobrar</td><td className="py-2 text-right font-bold text-amber-700">{formatBRL(totalCasa)}</td></tr></tfoot>
                    </table>
                  </div>
                )}
              </Secao>

              <Secao icon={Package} titulo="Estoque familiar disponível">
                {estoqueFamiliar.length === 0 ? <p className="text-sm text-slate-400 py-4 text-center">Nenhum item.</p> : (
                  <div className="space-y-1.5">
                    {estoqueFamiliar.map((x) => (
                      <div key={x.p.id} className="flex items-center justify-between text-sm">
                        <span className="text-slate-700">{x.p.nome}</span>
                        <span className={`font-medium ${x.qtd <= 0 ? "text-red-600" : "text-slate-600"}`}>{x.qtd} {x.p.unidade}</span>
                      </div>
                    ))}
                  </div>
                )}
              </Secao>

              <Secao icon={ClipboardList} titulo="Itens fornecidos pela família">
                {meusFornec.length === 0 ? <p className="text-sm text-slate-400 py-4 text-center">Nenhum fornecimento.</p> : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="text-left text-slate-400 border-b border-slate-100">
                        <th className="py-2 font-medium">Data</th><th className="py-2 font-medium">Produto</th><th className="py-2 font-medium text-right">Qtd</th>
                      </tr></thead>
                      <tbody className="divide-y divide-slate-50">
                        {meusFornec.map((f) => (
                          <tr key={f.id}><td className="py-2 text-slate-500">{formatDate(f.data)}</td><td className="py-2 text-slate-700">{getProduto(f.produtoId)?.nome || "-"}</td><td className="py-2 text-right text-slate-700">{f.quantidade}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Secao>

              <Secao icon={PackageX} titulo="Consumo do residente" tint="text-red-500">
                {meusConsumos.length === 0 ? <p className="text-sm text-slate-400 py-4 text-center">Nenhum consumo.</p> : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="text-left text-slate-400 border-b border-slate-100">
                        <th className="py-2 font-medium">Data</th><th className="py-2 font-medium">Produto</th><th className="py-2 font-medium text-right">Qtd</th><th className="py-2 font-medium">Origem</th>
                      </tr></thead>
                      <tbody className="divide-y divide-slate-50">
                        {meusConsumos.map((c) => (
                          <tr key={c.id}>
                            <td className="py-2 text-slate-500">{formatDate(c.data)}</td>
                            <td className="py-2 text-slate-700">{getProduto(c.produtoId)?.nome || "-"}</td>
                            <td className="py-2 text-right text-slate-700">{c.quantidade}</td>
                            <td className="py-2 text-xs text-slate-500">{c.qtdEmpresa > 0 ? (c.qtdResidente > 0 ? "Família + Casa" : "Casa de Repouso") : "Família"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Secao>
            </div>
          </>
        )}
        {tab === "senha" && <PortalTrocarSenha />}
      </main>

      <FloatingChat />
    </div>
  );
};

const PortalTrocarSenha = () => {
  const { trocarSenha } = useApp();
  const { toast } = useToast();
  const [f, setF] = useState({ atual: "", nova: "", confirma: "" });
  const [saving, setSaving] = useState(false);
  const salvar = async () => {
    if (!f.atual || !f.nova) { toast({ title: "Preencha a senha atual e a nova senha" }); return; }
    if (f.nova.length < 6) { toast({ title: "A nova senha deve ter pelo menos 6 caracteres" }); return; }
    if (f.nova !== f.confirma) { toast({ title: "A confirmação não confere" }); return; }
    setSaving(true);
    try {
      await trocarSenha(f.atual, f.nova);
      toast({ title: "Senha alterada com sucesso" });
      setF({ atual: "", nova: "", confirma: "" });
    } catch (e) {
      toast({ title: "Não foi possível alterar", description: e?.response?.data?.detail || "Verifique a senha atual." });
    } finally { setSaving(false); }
  };
  const cls = "w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-500";
  return (
    <div className="max-w-md mx-auto space-y-4" data-testid="familia-senha">
      <FotoPerfilCard />
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 space-y-4">
      <h2 className="font-semibold text-slate-800 flex items-center gap-2"><KeyRound className="w-4 h-4 text-amber-600" /> Alterar minha senha</h2>
      <div className="space-y-1.5"><label className="text-xs font-medium text-slate-500">Senha atual</label><input type="password" className={cls} value={f.atual} onChange={(e) => setF((x) => ({ ...x, atual: e.target.value }))} data-testid="familia-senha-atual" /></div>
      <div className="space-y-1.5"><label className="text-xs font-medium text-slate-500">Nova senha (mín. 6 caracteres)</label><input type="password" className={cls} value={f.nova} onChange={(e) => setF((x) => ({ ...x, nova: e.target.value }))} data-testid="familia-senha-nova" /></div>
      <div className="space-y-1.5"><label className="text-xs font-medium text-slate-500">Confirmar nova senha</label><input type="password" className={cls} value={f.confirma} onChange={(e) => setF((x) => ({ ...x, confirma: e.target.value }))} data-testid="familia-senha-confirma" /></div>
      <Button className="bg-blue-600 hover:bg-blue-700" onClick={salvar} disabled={saving} data-testid="familia-senha-salvar">{saving ? "Salvando..." : "Alterar senha"}</Button>
      </div>
    </div>
  );
};

export default FamiliaPortal;
